import {Flex, Span} from 'exsportia-components'
import {FC} from 'react'

import {SUBSCRIPTION_DURATION_TYPE_MAP} from '../../../api/subscriptions/constants'
import {
  AmountByTypeEnum,
  CalculatedSubscriptionActivity,
  PriceTypeEnum,
  SubscriptionActivity,
} from '../../../api/subscriptions/types'
import {safeRound} from '../../../helpers/logic'
import {TranslatorScope} from '../../../locale/types'
import {DotDivider} from '../../../ui/components/dot-divider'
import {useLocale, useLocaleType} from '../../../utils/i18n'
import {isNotNilAndNotEmpty} from '../../../utils/logic'
import {formatMinutes} from '../../../utils/time'

type Props = {
  benefit: SubscriptionActivity;
  withoutSubText?: boolean;
}

type Price = {
  price?: number;
  l: useLocaleType
}


type DiscountTextType = {
  [key in PriceTypeEnum]: (params: Price) => JSX.Element
}

export const priceTypeComponents: DiscountTextType = {
  [PriceTypeEnum.FREE]: ({l}) => (
    <Span>{l('free')}</Span>
  ),
  [PriceTypeEnum.BY_PERCENTAGE]: ({price}) => (
    <Span>{`-${price}%`}</Span>
  )
}

type PropsPeriod = {
  benefit: CalculatedSubscriptionActivity | SubscriptionActivity
}

/* const limitationsKeyMap = {
  [AmountByTypeEnum.ENTRIES]: 'entries',
  [AmountByTypeEnum.MINUTES]: 'minutes',
  [AmountByTypeEnum.CREDITS]: 'credits',
}*/

const amountLabelGetterMap: {[key in AmountByTypeEnum]: (amount: number, l: useLocaleType) => string} = {// TODO: ADD LOCALE
  [AmountByTypeEnum.ENTRIES]: (amount, l) => l('entries_single', {
    value: amount || '',
    declension: ['entries_single_genitive', 'entries_plural'] as [TranslatorScope, TranslatorScope]
  }),
  [AmountByTypeEnum.MINUTES]: (amount) => formatMinutes(amount),
  [AmountByTypeEnum.CREDITS]: (amount, l) => l('credits_single', {
    value: amount || '',
    declension: ['credits_single_genitive', 'credits_plural'] as [TranslatorScope, TranslatorScope]
  }),
}

const limitationsKeyMap: {
  [key in AmountByTypeEnum]: 'credits' | 'entries' | 'minutes'
} = {
  [AmountByTypeEnum.ENTRIES]: 'entries',
  [AmountByTypeEnum.MINUTES]: 'minutes',
  [AmountByTypeEnum.CREDITS]: 'credits',
}

const isCalculatedSubscriptionActivity = (benefit: SubscriptionActivity): benefit is CalculatedSubscriptionActivity => {
  return 'usedLimitations' in benefit
}

export const PeriodLabel: FC<PropsPeriod> = ({benefit}) => {
  const {l} = useLocale()
  const amount = benefit.frequencyAmount
  const amountLabel = amountLabelGetterMap[benefit.amountByType || AmountByTypeEnum.ENTRIES](amount, l)
  const period = (!benefit.unlimited && benefit.frequencyType)
    ? SUBSCRIPTION_DURATION_TYPE_MAP[benefit.frequencyType].profileLabel
    : 'period'
  let leftLabel = ''
  const withUsage = isCalculatedSubscriptionActivity(benefit)
  if (withUsage) {
    const usedLimitationsKey = limitationsKeyMap[benefit.amountByType || AmountByTypeEnum.ENTRIES]
    const usedLimitations = benefit.usedLimitations
    const left = isNotNilAndNotEmpty(usedLimitations)
      ? safeRound(Math.max(amount - usedLimitations[usedLimitationsKey], 0))
      : 0
    leftLabel = (benefit.amountByType === AmountByTypeEnum.MINUTES)
      ? formatMinutes(left)
      : String(left)

  }

  return (
    <>
      {
        benefit.unlimited
          ? (
            <Span color={'success'}>
              {l('unlimited')}
            </Span>
          )
          : withUsage
            ? (
              <Span color={'primary'}>
                {leftLabel}
                <Span color={'text-default'}>
                  {` / ${amountLabel}`}
                </Span>
              </Span>
            )
            : (
              <Span color={'primary'}>
                {amountLabel}
                <Span>
                  {` / ${l(period)}`}
                </Span>
              </Span>
            )
      }
    </>
  )
}

export const BenefitFeatures: FC<Props> = ({benefit}) => { // TODO: ADD LOCALE
  const {l} = useLocale()
  const PriceType = priceTypeComponents[benefit.priceType]

  return (
    <Flex alignItems={'center'} flexDirection={'row'}>
      <PeriodLabel benefit={benefit} />
      <DotDivider />
      <PriceType price={benefit.discount} l={l} />
      {
        benefit.useForParticipants
        && (
          <>
            <DotDivider/>
            <Span variant={'footnote'} color={'success'}>
              {l('shareable', {caseAction: 'capitalize'})}
            </Span>
          </>
        )
      }
    </Flex>
  )
}